import { Marker, Popup, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import type { Runner, LocationPoint } from '../../types/index'

interface RunnerMarkerProps {
  runner: Runner
  location: LocationPoint
  isCurrent: boolean
  isProjected?: boolean
}

export function RunnerMarker({ runner, location, isCurrent, isProjected = false }: RunnerMarkerProps) {
  const ageMs = Date.now() - new Date(location.timestamp).getTime()
  const ageMin = Math.floor(ageMs / 60000)
  const isStale = ageMs > 5 * 60 * 1000
  const isVeryStale = ageMs > 30 * 60 * 1000

  const lastSeenLabel = ageMin < 1 ? 'Just now'
    : ageMin < 60 ? `${ageMin} min ago`
    : `${Math.floor(ageMin / 60)}h ${ageMin % 60}m ago`

  const iconColor = isProjected ? '#93c5fd'
    : isVeryStale ? '#6b7280'
    : isStale ? '#9ca3af'
    : '#3b82f6'

  const iconHtml = `
    <div style="
      background-color: ${iconColor};
      border: 3px ${isProjected ? 'dashed' : 'solid'} white;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      color: white;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      opacity: ${isVeryStale ? '0.5' : isProjected ? '0.95' : '1'};
    ">
      ${runner.name.trim()[0].toUpperCase()}
    </div>
  `

  const icon = L.divIcon({
    html: iconHtml,
    iconSize: [32, 32],
    className: 'custom-div-icon',
  })

  const accuracyFt = location.accuracy ? Math.round(location.accuracy * 3.28084) : null
  const accuracyColor = !accuracyFt ? 'gray'
    : accuracyFt <= 30 ? 'green'
    : accuracyFt <= 50 ? 'yellow'
    : 'red'

  return (
    <Marker position={[location.latitude, location.longitude]} icon={icon}>
      <Tooltip sticky direction="top" offset={[0, -15]}>
        <div className="font-semibold">
          Runner {runner.runnerNumber}: {runner.name}
          {isProjected && <span className="text-orange-500"> (est.)</span>}
          {isStale && !isProjected && <span style={{ color: '#dc2626' }}> · stale</span>}
        </div>
        <div className="text-xs mt-1">Van {runner.vanNumber} · {lastSeenLabel}</div>
      </Tooltip>
      <Popup>
        <div style={{ fontSize: 12, lineHeight: '1.4' }}>
          <div style={{ fontWeight: 700, marginBottom: 3 }}>Runner {runner.runnerNumber} · {runner.name}</div>
          <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 4 }}>Van {runner.vanNumber}</div>
          {isProjected ? (
            <div style={{ color: '#d97706', fontSize: 11, fontWeight: 600 }}>⚠ Estimated position</div>
          ) : (
            <div style={{ fontSize: 11, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', color: '#374151' }}>
              <span style={{ color: '#6b7280' }}>Accuracy</span>
              <span style={{ fontWeight: 600, color: accuracyColor }}>{accuracyFt ? `±${accuracyFt}ft` : 'N/A'}</span>
              <span style={{ color: '#6b7280' }}>Coords</span>
              <span style={{ fontWeight: 600 }}>{location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}</span>
              <span style={{ color: '#6b7280' }}>Last ping</span>
              <span style={{ fontWeight: 600, color: isStale ? '#dc2626' : '#16a34a' }}>{lastSeenLabel}</span>
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  )
}
